import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { cloneDeep } from 'lodash';
import { trackEvent } from '../../../utils/tracking'
import { MultiSelectListboxOptionI } from '../../../types/ui/listBoxes';
interface UploadingFile {
    fileName: string;
    documentId: string;
    status: 'uploading' | 'processing';
    isAddingFromAPIResponse?: boolean;
}

interface FetchKnowledgeStateI {
    isUploadModalOpen: boolean;
    isAddNewCategoryBtnClicked: boolean;
    uploadingFiles: UploadingFile[];
    allDepartmentOptions: MultiSelectListboxOptionI[];
    selectedDepartmentOptions : {
        isInitiallyLoaded: boolean;
        options: MultiSelectListboxOptionI[];
    };
    documentsSearchTerm: string;
};

const initialState: FetchKnowledgeStateI = {
    isUploadModalOpen: false,
    isAddNewCategoryBtnClicked: false,
    uploadingFiles: [],
    allDepartmentOptions: [],
    selectedDepartmentOptions : {
        isInitiallyLoaded: false,
        options: [],
    },
    documentsSearchTerm: ''
};

export const fetchKnowledgeSlice = createSlice({
    name: 'fetch-knowledge',
    initialState,
    reducers: {
        setIsUploadModalOpen: (state, action: PayloadAction<boolean>) => {
            state.isUploadModalOpen = action.payload;
        },
        setIsAddNewCategoryBtnClicked: (state, action: PayloadAction<boolean>) => {
            state.isAddNewCategoryBtnClicked = action.payload;
        },
        setAllDepartmentOptions: (state, action: PayloadAction<MultiSelectListboxOptionI[]>) => {
            state.allDepartmentOptions = action.payload;
        },
        setSelectedDepartmentOptions: (state, action: PayloadAction<{ isInitiallyLoaded: boolean; options: MultiSelectListboxOptionI[] }>) => {
            state.selectedDepartmentOptions  = action.payload;
        },
        setDocumentsSearchTerm: (state, action: PayloadAction<string>) => {
            state.documentsSearchTerm = action.payload;
        },
        addUploadingFile: (state, action: PayloadAction<UploadingFile>) => {
            const fileExists = state.uploadingFiles.find((file) =>
                file.documentId === action.payload.documentId
            )
            if (!fileExists) {
                if (!action.payload.isAddingFromAPIResponse) {
                    state.uploadingFiles = [action.payload, ...state.uploadingFiles]
                } else {
                    state.uploadingFiles = [...state.uploadingFiles, action.payload]
                }
            } else {
                if (fileExists.status !== action.payload.status) {
                    updateUploadingFile({ documentId: action.payload.documentId, status: action.payload.status });
                }
            }
            trackEvent('Upload Document', {
                documentId: action.payload.documentId,
                fileName: action.payload.fileName,
                status: action.payload.status
            });
        },
        updateUploadingFile: (state, action: PayloadAction<{ documentId: string; status: 'uploading' | 'processing'; newDocumentId?: string }>) => {
            const clonedUploadingFiles = cloneDeep(state.uploadingFiles);
            const indexOfFile = clonedUploadingFiles.findIndex(file => file.documentId === action.payload.documentId);
            if (indexOfFile !== -1) {
                if (action.payload.newDocumentId) {
                    clonedUploadingFiles[indexOfFile].documentId = action.payload.newDocumentId
                }
                clonedUploadingFiles[indexOfFile].status = action.payload.status;
                state.uploadingFiles = clonedUploadingFiles;
            }
        },
        removeUploadingFile: (state, action: PayloadAction<{ documentId: string }>) => {
            state.uploadingFiles = state.uploadingFiles.filter(file => file.documentId !== action.payload.documentId);
        },
        removeUploadingFiles: (state, action: PayloadAction<{ documentIds: string[] }>) => {
            state.uploadingFiles = state.uploadingFiles.filter(file => !action.payload.documentIds.includes(file.documentId));
        },
        clearFetchKnowledgeState: (state) => {
            state.isUploadModalOpen = false;
            state.isAddNewCategoryBtnClicked = false;
            state.uploadingFiles = [];
            state.allDepartmentOptions = [];
            state.selectedDepartmentOptions  = {
                isInitiallyLoaded: false,
                options: [],
            };
            state.documentsSearchTerm = '';
        },
    },
})

export const {
    setIsUploadModalOpen,
    setIsAddNewCategoryBtnClicked,
    setAllDepartmentOptions,
    setSelectedDepartmentOptions,
    setDocumentsSearchTerm,
    clearFetchKnowledgeState,
    addUploadingFile,
    updateUploadingFile,
    removeUploadingFile,
    removeUploadingFiles,
} = fetchKnowledgeSlice.actions;

export default fetchKnowledgeSlice.reducer;